import { useLocation, useNavigate } from 'react-router-dom'

import { useMutation } from '@apollo/client'
import { Box, Button } from '@factorialco/deprecated-design-system'
import {
  ItAssetDetailsQuery,
  ItAssetsTableQuery,
  UpdateItAssetDocument,
} from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

// Union type to handle both table and details assets
type Asset =
  | ItAssetsTableQuery['itManagement']['itAssetsConnection']['nodes'][number]
  | ItAssetDetailsQuery['itManagement']['itAsset']

interface Props {
  asset: Asset
  onClose: () => void
}

export const RecoverItAssetModal = ({ asset, onClose }: Props) => {
  const navigate = useNavigate()
  const location = useLocation()
  const { navigation } = useNavigation()
  const [updateItAsset, { loading: updateLoading }] = useMutation(UpdateItAssetDocument)

  const handleRecoverAsset = async () => {
    if (!asset) return

    try {
      await updateItAsset({
        variables: {
          id: asset.id,
          currency: 'currency' in asset ? String(asset.currency) : undefined,
          itAssetModelId: asset.itAssetModel?.id || '',
          label: 'label' in asset ? asset.label : undefined,
          purchaseDate: asset.purchaseDate,
          purchasePriceCents: asset.purchasePriceCents,
          serialNumber: asset.serialNumber,
          status: 'in_stock',
          warrantyEndDate: asset.warrantyEndDate,
        },
        refetchQueries: ['ItAssetsTable', 'ItAssetDetails', 'ItAssetsStatusCounts'],
        awaitRefetchQueries: true,
      })
      app.setFlash({
        type: 'correct',
        message: i18n.t('it_management.assets.restore_modal.success_message'),
      })
      onClose()
      // Redirect to inventory page only when recovering from details page
      if (
        location.pathname.includes('/it-management/') &&
        location.pathname.split('/').length > 2
      ) {
        navigate(navigation.it_management.fullPath())
      }
    } catch (error) {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('it_management.assets.restore_modal.error_message'),
      })
    }
  }

  const assetName = asset
    ? asset.itAssetModel?.brand && asset.itAssetModel?.name
      ? `${asset.itAssetModel.brand} ${asset.itAssetModel.name}`
      : asset.serialNumber || 'Unknown Asset'
    : 'Unknown Asset'

  return (
    <Modal
      title={i18n.t('it_management.assets.restore_modal.title')}
      onClose={onClose}
      illustration={'archive'}
      description={i18n.t('it_management.assets.restore_modal.description', { assetName })}
      onSubmit={handleRecoverAsset}
      footer={
        <Box gap="s8" flexDirection="row" alignItems="center" justifyContent="flexEnd">
          <Button type="secondary" onClick={onClose} disabled={updateLoading}>
            {i18n.t('common.cancel')}
          </Button>

          <Button type="primary" submit disabled={updateLoading}>
            {updateLoading
              ? i18n.t('common.saving')
              : i18n.t('it_management.assets.restore_modal.confirm_button')}
          </Button>
        </Box>
      }
    />
  )
}
